import { Characteristic } from './characteristic.entity';
import { Consent } from './consent.entity';
import { Gender } from '../enums/gender';
import { Pairing } from './pairing.entity';
import { Serializable } from './serializable';
import { UserRole } from '../enums/user-role';

export class User implements Serializable<User> {
  private _id = '';
  public email = '';
  public firstName = '';
  public lastName = '';
  public role = '';
  public gender: Gender = null;
  public lastPwdSetDate: Date = null;
  public emailVerificationDate: Date = null;
  public activationDate: Date = null;
  public allowSynchronization = true;
  public isEnabled = true;
  public isInClinicalStudy = false;
  public activationCode = '';
  public creationDate: Date = null;
  public lastLoginDate: Date = null;
  public lastUploadDate: Date = null;
  public characteristics: Characteristic[] = [];
  public consents: Consent[] = [];
  public pairing: Pairing = null;

  /**
   * Gets entity id
   * @returns entity id
   */
  public getId(): string {
    return this._id;
  }

  /**
   * Transforms received object to User entity
   * @param input
   * @returns User entity
   */
  public deserialize(input: any = {}): User {
    if (!input) {
      return null;
    }

    const properties = Object.getOwnPropertyNames(this);
    Object.keys(input).forEach((key) => {
      if (input[key] !== null) {
        switch (key) {
          case 'id':
            this._id = input[key];
            break;
          case 'characteristics':
            input[key].forEach((characteristic) => {
              const property = properties.find(
                (item) =>
                  item.toLowerCase() ==
                  characteristic.characteristicType.code.toLowerCase()
              );
              if (property !== undefined) {
                switch (typeof this[property]) {
                  case 'number':
                    this[property] = parseInt(characteristic.value, 10);
                    break;
                  case 'boolean':
                    this[property] = characteristic.value === 'true';
                    break;
                  default:
                    this[property] = characteristic.value;
                    break;
                }
                this[key].push(
                  new Characteristic().deserialize(characteristic)
                );
              }
            });
            break;
          case 'consents':
            input[key]
              .sort((a, b) => {
                return (
                  new Date(a.approvalDate).getTime() -
                  new Date(b.approvalDate).getTime()
                );
              })
              .forEach((consent) => {
                const property = properties.find(
                  (item) =>
                    item.toLowerCase() == consent.consentType.toLowerCase()
                );
                if (property !== undefined) {
                  this[property] = consent.approved;
                  this[key].push(new Consent().deserialize(consent));
                }
              });
            break;
          case 'lastPwdSetDate':
          case 'emailVerificationDate':
          case 'activationDate':
          case 'creationDate':
          case 'lastLoginDate':
          case 'lastUploadDate':
            this[key] = new Date(input[key]);
            break;
          case 'pairing':
            this[key] = new Pairing().deserialize(input[key]);
            break;
          default:
            if (properties.includes(key)) {
              this[key] = input[key];
            }
            break;
        }
      }
    });

    return this;
  }

  /**
   * Transforms User entity to json object
   * @returns json object
   */
  public serialize(): any {
    const output = {};

    Object.getOwnPropertyNames(this).forEach((property) => {
      switch (property) {
        case '_id':
          if (this[property]) {
            output['id'] = this[property];
          }
          break;
        case 'characteristics':
          this[property].forEach((characteristic) => {
            output[property].push(characteristic.serialize());
          });
          break;
        case 'consents':
          this[property].forEach((consent) => {
            output[property].push(consent.serialize());
          });
          break;
        case 'lastPwdSetDate':
        case 'emailVerificationDate':
        case 'activationDate':
          output[property] =
            this[property] != null ? this[property].toString() : this[property];
          break;
        default:
          output[property] = this[property];
          break;
      }
    });

    return output;
  }

  /**
   * Updates a characteristic of user
   * @param characteristic
   */
  public updateCharacteristic(characteristic: Characteristic): void {
    const properties = Object.getOwnPropertyNames(this);

    // Update property
    const property = properties.find(
      (item) =>
        item.toLowerCase() ==
        characteristic.characteristicType.code.toLowerCase()
    );

    if (property !== undefined) {
      this[property] = characteristic.value;

      // Add/Update characteristics list
      if (characteristic.getId()) {
        const index = this.characteristics.findIndex(
          (item) =>
            item.characteristicType.code.toLowerCase() ==
            characteristic.characteristicType.code.toLowerCase()
        );
        index > -1
          ? (this.characteristics[index] = characteristic)
          : this.characteristics.push(characteristic);
      }
    }
  }

  public updateConsent(consent: Consent): void {
    const properties = Object.getOwnPropertyNames(this);

    // Update property
    const property = properties.find(
      (item) => item.toLowerCase() == consent.consentType.toLowerCase()
    );
    if (property !== undefined) {
      this[property] = consent.approved;

      // Add/Update consent list
      if (consent.getId()) {
        const index = this.consents.findIndex(
          (item) =>
            item.consentType.toLowerCase() == consent.consentType.toLowerCase()
        );
        index > -1
          ? (this.consents[index] = consent)
          : this.consents.push(consent);
      }
    }
  }
}
